import { AppBar as MuiAppBar, Toolbar, IconButton, Typography, Box, Button, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HelpIcon from '@mui/icons-material/Help';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';

// Logo component using the Pyper logo image
const FogLogo = () => {
  return (
    <Box component="div" sx={{ 
      height: { xs: 28, sm: 36, md: 44 },
      display: 'flex', 
      alignItems: 'center',
      justifyContent: 'flex-start',
      minWidth: { xs: 80, sm: 120, md: 150 },
    }}>
      <img 
        src="/pypervision-logo.png" 
        alt="PyperVision Logo" 
        style={{ 
          height: '100%',
          width: 'auto',
          objectFit: 'contain',
        }} 
        onError={(e) => {
          // Fallback to the old logo if the new one fails to load
          e.currentTarget.src = "/pyper-final-logo-thinner-lines-white.png";
        }}
      />
    </Box>
  );
};

// Custom styled Typography for the FogCast title with responsive sizing
const BrandTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Montserrat', 'Roboto', 'Helvetica', 'Arial', sans-serif",
  fontWeight: 800,
  letterSpacing: '0.01em',
  fontSize: '1.8rem', // Base size for mobile
  background: 'linear-gradient(45deg, #4dd0e1 30%, #80deea 90%)',
  backgroundClip: 'text',
  textFillColor: 'transparent',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  [theme.breakpoints.up('sm')]: {
    fontSize: '2.3rem', // Medium size for tablets
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '2.8rem', // Original size for desktops
  },
}));

interface AppBarProps {
  onMenuClick?: () => void;
}

const AppBar = ({ onMenuClick }: AppBarProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <MuiAppBar 
      position="static" 
      color="transparent" 
      elevation={0} 
      sx={{ 
        borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
        py: { xs: 0.5, sm: 0.5, md: 0 } // Add padding on smaller screens
      }}
    >
      <Toolbar sx={{ 
        justifyContent: 'space-between',
        minHeight: { xs: 56, sm: 64 }, // Smaller height on mobile
        px: { xs: 0.5, sm: 2 } // Less horizontal padding on mobile
      }}>
        {/* Left: Logo */}
        <Box sx={{ 
          flex: { xs: '0 0 auto', md: 1 }, 
          display: 'flex', 
          justifyContent: 'flex-start',
          mr: { xs: 0.5, sm: 2 }
        }}>
          <FogLogo />
        </Box>
        
        {/* Center: Title */}
        <Box sx={{ 
          flex: { xs: 1, md: 1 }, 
          display: 'flex', 
          justifyContent: { xs: 'center', md: 'center' },
          position: 'relative',
          left: { xs: isMobile ? '-10px' : 0, sm: 0 } // Slight adjustment for mobile centering
        }}>
          <RouterLink to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            <BrandTitle>
              FogCast
            </BrandTitle>
          </RouterLink>
        </Box>
        
        {/* Right: Auth, Menu and Help */}
        <Box sx={{ 
          flex: { xs: '0 0 auto', md: 1 }, 
          display: 'flex', 
          justifyContent: 'flex-end', 
          alignItems: 'center',
          ml: { xs: 0.5, sm: 2 },
          gap: 1
        }}>
          <Button 
            component={RouterLink} 
            to="/help"
            color="inherit"
            startIcon={<HelpIcon />}
            sx={{ 
              mr: { xs: 0.5, sm: 1, md: 2 },
              minWidth: { xs: 'auto', sm: 'auto' },
              px: { xs: 0.5, sm: 1.5 }
            }}
          >
            {!isMobile && 'Help'}
          </Button>
          <IconButton 
            edge="end" 
            color="inherit" 
            aria-label="menu"
            onClick={onMenuClick}
            size={isMobile ? 'medium' : 'large'}
            sx={{ 
              '& .MuiSvgIcon-root': { 
                fontSize: { xs: 24, sm: 28, md: 32 } 
              },
              p: { xs: 0.5, sm: 1 },
              mr: { xs: 0, sm: 0.5 }
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      </Toolbar>
    </MuiAppBar>
  );
};

export default AppBar; 